import { useState, memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Shortlist, Player } from "@/lib/types";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePlayerDeletion } from "@/hooks/usePlayerDeletion";
import { PlayerDeletionDialog } from "./PlayerDeletionDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";

interface BulkActionsBarProps {
  selectedPlayerIds: number[];
  totalPlayers: number;
  onClearSelection: () => void;
  shortlists: Shortlist[];
  players: Player[];
}

const BulkActionsBar = memo(function BulkActionsBar({
  selectedPlayerIds,
  totalPlayers,
  onClearSelection,
  shortlists,
  players,
}: BulkActionsBarProps) {
  const { toast } = useToast();
  const { removeManyPlayers, isRemoving } = usePlayerDeletion();
  const [showAddToShortlistDialog, setShowAddToShortlistDialog] = useState(false);
  const [showCreateShortlistDialog, setShowCreateShortlistDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedShortlistId, setSelectedShortlistId] = useState<string>("");
  const [newShortlistName, setNewShortlistName] = useState("");
  const [newShortlistDescription, setNewShortlistDescription] = useState("");

  // Get selected players
  const selectedPlayers = players.filter(p => selectedPlayerIds.includes(p.id));

  // Bulk add to existing shortlist mutation
  const bulkAddToShortlistMutation = useMutation({
    mutationFn: async ({
      shortlistId,
      playerIds,
    }: {
      shortlistId: number;
      playerIds: number[];
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/shortlists/${shortlistId}/players/bulk`,
        { playerIds }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to add players to shortlist");
      }
      return response.json();
    },
    onSuccess: (data) => {
      const { added_count, skipped_count, errors } = data;
      let message = `Added ${added_count} players to shortlist`;
      if (skipped_count > 0) {
        message += `, ${skipped_count} already in shortlist`;
      }
      if (errors.length > 0) {
        message += `, ${errors.length} errors`;
      }
      
      toast({
        title: "Bulk add completed",
        description: message,
      });
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      queryClient.invalidateQueries({ queryKey: [`/api/shortlists/${selectedShortlistId}`] });
      
      // Clear selection and close dialog
      onClearSelection();
      setShowAddToShortlistDialog(false);
      setSelectedShortlistId("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add players to shortlist",
        variant: "destructive",
      });
    },
  });

  // Create new shortlist with players mutation
  const createShortlistWithPlayersMutation = useMutation({
    mutationFn: async ({
      name,
      description,
      playerIds,
    }: {
      name: string;
      description?: string;
      playerIds: number[];
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/shortlists/create-with-players`,
        { name, description, playerIds }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create shortlist");
      }
      return response.json();
    },
    onSuccess: (data) => {
      const { shortlist, added_count, errors } = data;
      let message = `Created "${shortlist.name}" with ${added_count} players`;
      if (errors.length > 0) {
        message += `, ${errors.length} errors`;
      }
      
      toast({
        title: "Shortlist created",
        description: message,
      });
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      
      // Clear selection and close dialog
      onClearSelection();
      setShowCreateShortlistDialog(false);
      setNewShortlistName("");
      setNewShortlistDescription("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create shortlist",
        variant: "destructive",
      });
    },
  });

  const handleAddToExistingShortlist = () => {
    if (!selectedShortlistId) {
      toast({
        title: "Error",
        description: "Please select a shortlist",
        variant: "destructive",
      });
      return;
    }

    bulkAddToShortlistMutation.mutate({
      shortlistId: parseInt(selectedShortlistId),
      playerIds: selectedPlayerIds,
    });
  };

  const handleCreateNewShortlist = () => {
    if (!newShortlistName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a shortlist name",
        variant: "destructive",
      });
      return;
    }

    createShortlistWithPlayersMutation.mutate({
      name: newShortlistName.trim(),
      description: newShortlistDescription.trim() || undefined,
      playerIds: selectedPlayerIds,
    });
  };

  if (selectedPlayerIds.length === 0) return null;

  return (
    <>
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Selection info and clear button */}
          <div className="flex items-center gap-4">
            <div className="text-sm font-medium text-primary">
              {selectedPlayerIds.length} of {totalPlayers} players selected
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onClearSelection}
              className="text-neutral-600 hover:text-neutral-800"
            >
              Clear selection
            </Button>
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-col xs:flex-row flex-wrap items-stretch xs:items-center gap-2 w-full xs:w-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAddToShortlistDialog(true)}
              disabled={shortlists.length === 0}
              className="justify-center xs:justify-start"
            >
              <i className="fas fa-star mr-2"></i>
              Add to Shortlist
            </Button>
            <Button
              size="sm"
              onClick={() => setShowCreateShortlistDialog(true)}
              className="justify-center xs:justify-start"
            >
              <i className="fas fa-plus mr-2"></i>
              Create New Shortlist
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              className="justify-center xs:justify-start"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </Button>
          </div>
        </div>
      </div>

      {/* Add to Existing Shortlist Dialog */}
      <Dialog open={showAddToShortlistDialog} onOpenChange={setShowAddToShortlistDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Existing Shortlist</DialogTitle>
            <DialogDescription>
              Add {selectedPlayerIds.length} selected players to an existing shortlist.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="shortlist-select" className="text-sm font-medium">
                Select Shortlist
              </label>
              <Select value={selectedShortlistId} onValueChange={setSelectedShortlistId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a shortlist..." />
                </SelectTrigger>
                <SelectContent>
                  {shortlists.map((shortlist) => (
                    <SelectItem key={shortlist.id} value={shortlist.id.toString()}>
                      {shortlist.name}
                      {shortlist.description && (
                        <span className="text-neutral-500 ml-2">• {shortlist.description}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddToShortlistDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToExistingShortlist}
              disabled={!selectedShortlistId || bulkAddToShortlistMutation.isPending}
            >
              {bulkAddToShortlistMutation.isPending ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-star mr-2"></i>
                  Add Players
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Shortlist Dialog */}
      <Dialog open={showCreateShortlistDialog} onOpenChange={setShowCreateShortlistDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Shortlist</DialogTitle>
            <DialogDescription>
              Create a new shortlist with {selectedPlayerIds.length} selected players.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="shortlist-name" className="text-sm font-medium">
                Name *
              </label>
              <Input
                id="shortlist-name"
                placeholder="Enter shortlist name..."
                value={newShortlistName}
                onChange={(e) => setNewShortlistName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="shortlist-description" className="text-sm font-medium">
                Description (optional)
              </label>
              <Textarea
                id="shortlist-description"
                placeholder="Enter description..."
                value={newShortlistDescription}
                onChange={(e) => setNewShortlistDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateShortlistDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewShortlist}
              disabled={!newShortlistName.trim() || createShortlistWithPlayersMutation.isPending}
            >
              {createShortlistWithPlayersMutation.isPending ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-plus mr-2"></i>
                  Create Shortlist
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Player Deletion Dialog */}
      <PlayerDeletionDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        players={selectedPlayers}
        onConfirm={(reason) => {
          removeManyPlayers({
            playerIds: selectedPlayerIds,
            reason,
          });
          setShowDeleteDialog(false);
          onClearSelection();
        }}
        isLoading={isRemoving}
      />
    </>
  );
});

export default BulkActionsBar;